import { useMemo } from "react";

import classNames from "classnames";
import urlJoin from "url-join";

import { useConfig } from "@jti/app-context/useConfig";
import type { FilterableProduct } from "@jti/commerce/features";
import { filterProductsByAttributes } from "@jti/commerce/features";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { QueryClient, QueryClientProvider } from "@jti/rest-api";
import { ProductCardContainerContextProvider } from "@jti/ui/components/ProductCardContainerContextProvider/ProductCardContainerContextProvider";
import { RtbCard } from "@jti/ui/components/RtbCard/RtbCard";
import { Typography } from "@jti/ui/components/Typography";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { ProductCard } from "@/components/ProductCard/ProductCard";
import { useFilter } from "@/context/FilterContext";
import { useRewriteUrls } from "@/context/RewriteUrlsContextProvider";
import type { ColorVariant } from "@/libs/commerce/products";
import { shouldShowQuickAddToCart } from "@/utils/quickAddToCart";

import { Grid } from "../Grid/Grid";

import type { CategoryItemsProps } from "./CategoryItems.inputs.builder";

import styles from "./CategoryItems.module.scss";

const client = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

interface ExtendedCategoryItemsProps extends CategoryItemsProps {
  subcategoriesMap?: Record<string, string[]>;
}

type CategoryItem = NonNullable<CategoryItemsProps["items"]>[number];

const EMPTY_SUBCATEGORIES_MAP: Record<string, string[]> = {};
const EMPTY_SELECTED_CATEGORY_UIDS: string[] = [];
const EMPTY_SELECTED_ATTRIBUTES: Record<string, string[]> = {};

const hasCategoryUid = (item: CategoryItem, categoryUid: string): boolean =>
  "categoryUid" in item && item.categoryUid === categoryUid;

const isListedUnderCategory = (item: CategoryItem, categoryUid: string): boolean =>
  "categories" in item &&
  Array.isArray(item.categories) &&
  item.categories.some((itemCategory) => itemCategory?.uid === categoryUid);

const isMappedToCategory = (subcategoriesMap: Record<string, string[]>, categoryUid: string, sku: string): boolean =>
  (subcategoriesMap[categoryUid] ?? []).includes(sku);

const isInAnySelectedCategory = (
  item: CategoryItem,
  selectedCategoryUids: string[],
  subcategoriesMap: Record<string, string[]>
): boolean => {
  const sku = item?.sku;
  if (!sku) return false;

  return selectedCategoryUids.some(
    (categoryUid) =>
      hasCategoryUid(item, categoryUid) ||
      isListedUnderCategory(item, categoryUid) ||
      isMappedToCategory(subcategoriesMap, categoryUid, sku)
  );
};

export function CategoryItems({
  items,
  columnDesktopWidth,
  columnTabletWidth,
  withRtbCard,
  rtbCard,
  subcategoriesMap = EMPTY_SUBCATEGORIES_MAP,
}: ExtendedCategoryItemsProps) {
  const { __ } = useTranslations();
  const url = useRewriteUrls();
  const { isQuickAddToCartCtaForSimpleSticksEnabled } = useConfig();

  // FilterContext may not be available (e.g. rendered outside FilterProvider), so use it optionally.
  const filterContext = useFilter({ optional: true });
  const selectedCategoryUids = filterContext?.filterState.selectedCategoryUids ?? EMPTY_SELECTED_CATEGORY_UIDS;
  const selectedAttributes = filterContext?.filterState.selectedAttributes ?? EMPTY_SELECTED_ATTRIBUTES;
  const clearAttributes = filterContext?.clearAttributes;
  const hasAttributeFilters = Object.keys(selectedAttributes).length > 0;

  // Client-side filtering: narrow by category first, then by product attributes.
  const filteredItems = useMemo(() => {
    if (!items) return items;

    const itemsInSelectedCategories =
      selectedCategoryUids.length === 0
        ? items
        : items.filter((item) => isInAnySelectedCategory(item, selectedCategoryUids, subcategoriesMap));

    return filterProductsByAttributes(
      itemsInSelectedCategories as (CategoryItem & FilterableProduct)[],
      selectedAttributes
    );
  }, [items, selectedCategoryUids, selectedAttributes, subcategoriesMap]);

  const shouldRenderProductCard = <T extends CategoryItem>(
    item: T
  ): item is T & {
    sku: string;
    name: string;
    colors?: ColorVariant[];
  } => {
    return Boolean(item.sku && item.name);
  };

  if (hasAttributeFilters && filteredItems?.length === 0) {
    return (
      <AlertBanner
        title={__("plp.filters.no_matches.title")}
        description={__("plp.filters.no_matches.description")}
        actions={clearAttributes ? [{ label: __("plp.filters.reset"), onClick: clearAttributes }] : undefined}
        data-testid="plp-filters-no-matches"
      />
    );
  }

  if (filteredItems?.length === 0 && url.shopGeographicalArea) {
    return <Typography variant="h4">{__("shop.no.matching.products.per.geographical.area")}</Typography>;
  }

  return (
    <QueryClientProvider client={client}>
      <ProductCardContainerContextProvider>
        <Grid columnDesktopWidth={columnDesktopWidth} columnTabletWidth={columnTabletWidth}>
          {filteredItems?.flatMap((item, index) => {
            const elements = [];
            const sanitizedDescription =
              "sanitizedDescription" in item && typeof item.sanitizedDescription === "string"
                ? item.sanitizedDescription
                : undefined;

            const isLastElement = filteredItems.length <= 2 && index === filteredItems.length - 1;
            const isThirdElement = filteredItems.length >= 3 && index === 2;

            let rtbNode = null;

            if (withRtbCard && rtbCard && (isLastElement || isThirdElement)) {
              const isFullWidthDesktop = columnDesktopWidth === "12";
              const isFullWidthTablet = !columnTabletWidth || columnTabletWidth === "12";

              const cardClassName = classNames(styles.card, {
                [styles.fullWidthDesktop as string]: isFullWidthDesktop,
                [styles.fullWidthTablet as string]: isFullWidthTablet,
              });

              rtbNode = <RtbCard key="rtb-card" className={cardClassName} {...rtbCard} />;
            }

            if (isThirdElement && rtbNode) {
              elements.push(rtbNode);
            }

            if (shouldRenderProductCard(item)) {
              const {
                name,
                url_key,
                thumbnailSrc,
                thumbnailAltText,
                shortDescription,
                sku,
                categoryUrlKey,
                isNew,
                badge,
                isCurrentPriceHidden,
                isDescriptionHidden,
                isDiscountBadgeHidden,
                isFreeTrialProduct,
                isNewBadgeHidden,
                isOldPriceHidden,
                isPrimaryButtonHidden,
                isProductColorVariantsHidden,
                primaryButton,
                secondaryButton,
                isOutOfStockLabelHidden,
                colors,
              } = item;

              const shopUrl = url.shopGeographicalArea || url.shop;
              const segments = ["/", shopUrl, categoryUrlKey ?? "", url_key].filter((value): value is string =>
                Boolean(value)
              );

              const href = urlJoin(segments);

              const newProductBadgeLabel = isNew && !isNewBadgeHidden ? __("product.badge.new") : "";

              const badgeLeft = badge?.left || newProductBadgeLabel;

              const showQuickAddToCart = shouldShowQuickAddToCart(
                item,
                isQuickAddToCartCtaForSimpleSticksEnabled ?? false
              );

              const primaryButtonConfig = {
                href: primaryButton?.href?.toLowerCase() || href,
                text: showQuickAddToCart
                  ? __("product.button.quickaddtocart")
                  : primaryButton?.text || __("product.button.buynow"),
                enabled: !isPrimaryButtonHidden,
              };

              elements.push(
                <ProductCard
                  key={sku}
                  sku={sku}
                  image={{
                    src: thumbnailSrc,
                    alt: thumbnailAltText,
                  }}
                  badge={{
                    left: badgeLeft,
                    right: badge?.right || "",
                    leftIcon: badge?.leftIcon,
                    rightIcon: badge?.rightIcon,
                  }}
                  title={name}
                  description={shortDescription}
                  sanitizedDescription={sanitizedDescription}
                  isCurrentPriceHidden={isCurrentPriceHidden}
                  isDescriptionHidden={isDescriptionHidden}
                  isDiscountBadgeHidden={isDiscountBadgeHidden}
                  isFreeTrialProduct={isFreeTrialProduct}
                  isOldPriceHidden={isOldPriceHidden}
                  isProductColorVariantsHidden={isProductColorVariantsHidden}
                  isOutOfStockLabelHidden={isOutOfStockLabelHidden}
                  imageFetchPriority={index < 2 ? "high" : "auto"}
                  secondaryButton={
                    secondaryButton?.href && secondaryButton.text
                      ? {
                          href: secondaryButton.href,
                          text: secondaryButton.text,
                        }
                      : undefined
                  }
                  primaryButton={primaryButtonConfig}
                  colors={colors}
                  shouldShowQuickAddToCart={showQuickAddToCart}
                />
              );
            }

            if (isLastElement && rtbNode) {
              elements.push(rtbNode);
            }

            return elements;
          })}
        </Grid>
      </ProductCardContainerContextProvider>
    </QueryClientProvider>
  );
}
